import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { PageHeader, Badge } from "../../components/ui-extras";
import { DownloadSimple } from "@phosphor-icons/react";
import { startDownload, backendDownloadUrl, downloadFromBackend } from "../../lib/download";
import { displayProduct } from "../../lib/products";
import { computeInvoiceTaxBreakdown } from "../../lib/tax";
import { InvoiceSummaryTotals } from "../../components/InvoiceSummaryTotals";
import { formatCard } from "../../lib/cardDisplay";

function invoicePdfHref(invoiceId) {
  return backendDownloadUrl(`/invoices/${invoiceId}/pdf`);
}

const downloadInvoicePdf = async (invoiceId) => {
  try {
    await downloadFromBackend(`/invoices/${invoiceId}/pdf`, {});
  } catch (_) {}
};

export default function CustomerInvoiceDetail() {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/invoices/${invoiceId}`).then(({ data }) => setData(data)); }, [invoiceId]);

  if (!data) return <div className="font-mono">LOADING…</div>;
  const { invoice, line_items } = data;

  return (
    <>
      <div className="mb-4"><Link to="/portal/invoices" className="overline hover:underline">← INVOICES</Link></div>
      <PageHeader
        title={invoice.invoice_number}
        subtitle="PORTAL / INVOICE"
        testId="portal-invoice-detail-header"
        actions={<button className="gf-btn gf-btn-primary" onClick={() => downloadInvoicePdf(invoiceId)}><DownloadSimple size={14}/> PDF</button>}
      />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="gf-card gf-card-body"><div className="overline">ISSUED</div><div className="font-mono mt-1">{invoice.issue_date}</div></div>
        <div className="gf-card gf-card-body"><div className="overline">DUE</div><div className="font-mono mt-1">{invoice.due_date}</div></div>
        <div className="gf-card gf-card-body"><div className="overline">TOTAL</div><div className="font-mono font-semibold mt-1">${invoice.total.toFixed(2)}</div></div>
        <div className="gf-card gf-card-body"><div className="overline">STATUS</div><div className="mt-1"><Badge variant={invoice.status === "paid" ? "success" : "info"}>{invoice.status}</Badge></div></div>
      </div>

      <div className="gf-card">
        <div className="gf-card-header"><h3>Line items</h3></div>
        {(() => {
          // Same tax breakdown helper the admin invoice detail uses — keeps
          // the customer portal and admin views in lockstep with the PDF.
          const { rows, totals, byProv, isUsa } = computeInvoiceTaxBreakdown(line_items);
          const provinces = Object.keys(byProv).sort();
          return (
            <>
              <table className="gf-table">
                <thead>
                  <tr>
                    <th>DATE</th><th>CARD</th><th>STATION</th><th>CITY</th><th>PROV</th><th>PRODUCT</th>
                    <th className="text-right" data-testid="invoice-col-qty">QTY ({isUsa ? "GAL" : "L"})</th><th className="text-right" data-testid="invoice-col-price">{isUsa ? "PRICE/GAL" : "PRICE/LTR"}</th>
                    <th className="text-right">AMOUNT</th>
                    <th className="text-right">GST/HST</th><th className="text-right">PST</th>
                    <th className="text-right">FINAL</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.transaction_id}>
                      <td className="font-mono text-xs">{t.tran_date}</td>
                      <td className="font-mono">{formatCard(t.card_number, t.network, t.card_last4)}</td>
                      <td className="text-xs">{t.station_name}</td>
                      <td className="text-xs">{t.city}</td>
                      <td className="font-mono text-xs">{(t.province || t.state || "").toUpperCase()}</td>
                      <td className="font-mono text-xs">{displayProduct(t.product, t.network)}</td>
                      <td className="gf-num">{t.qty?.toFixed(2)}</td>
                      <td className="gf-num">{t.customer_cpl?.toFixed(4)}</td>
                      <td className="gf-num">${t.customer_amount?.toFixed(2)}</td>
                      <td className="gf-num" data-testid={`invoice-line-gst-${t.transaction_id}`}>${t.gst.toFixed(2)}</td>
                      <td className="gf-num" data-testid={`invoice-line-pst-${t.transaction_id}`}>${t.pst.toFixed(2)}</td>
                      <td className="gf-num">${t.finalAmount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#F4F4F5" }}>
                    <td colSpan={6} className="text-right font-mono font-bold">TOTAL</td>
                    <td className="gf-num font-bold">{totals.qty.toFixed(2)}</td>
                    <td></td>
                    <td className="gf-num font-bold">${totals.amount.toFixed(2)}</td>
                    <td className="gf-num font-bold">${totals.gst.toFixed(2)}</td>
                    <td className="gf-num font-bold">${totals.pst.toFixed(2)}</td>
                    <td className="gf-num font-bold">${totals.finalAmount.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>

              {provinces.length > 0 && (
                <div className="p-4 border-t border-zinc-200">
                  <div className="overline mb-2">TAX BREAKDOWN BY PROVINCE</div>
                  <table className="gf-table" style={{ maxWidth: 480 }}>
                    <thead>
                      <tr>
                        <th>PROVINCE</th>
                        <th className="text-right">GST/HST</th>
                        <th className="text-right">PST</th>
                        <th className="text-right">SUBTOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {provinces.map((p) => (
                        <tr key={p}>
                          <td className="font-mono">{p}</td>
                          <td className="gf-num">${byProv[p].gst.toFixed(2)}</td>
                          <td className="gf-num">${byProv[p].pst.toFixed(2)}</td>
                          <td className="gf-num">${(byProv[p].gst + byProv[p].pst).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: "#F4F4F5" }}>
                        <td className="font-mono font-bold">TOTAL</td>
                        <td className="gf-num font-bold">${totals.gst.toFixed(2)}</td>
                        <td className="gf-num font-bold">${totals.pst.toFixed(2)}</td>
                        <td className="gf-num font-bold">${(totals.gst + totals.pst).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <div className="p-4 border-t border-zinc-200 flex justify-end">
                <InvoiceSummaryTotals invoice={invoice} totals={totals} isUsa={isUsa} />
              </div>
            </>
          );
        })()}
      </div>
    </>
  );
}
